import { Clock, CornerUpLeft, Pencil, Pin, Trash, User } from "lucide-react";
import { EntityType } from "@repo/types/enums";
import { TimelineActivityType } from "../../../types/enums";
import { ActivityActionType } from "../../../types/enums/ui/ActivityActionType";
import type { KeyValueItem } from "@repo/types/ui/keyValuePairs";
import { KeyValueViewType } from "@repo/types/ui/keyValuePairs";
import type { TimelineActivityDTO } from "../../../types/dtos/TimelineActivityDTO";
import useGetTicketEmailMessages from "../../../hooks/queries/useEmailMessages";
import KeyValueMenu from "../../ui/menu/KeyValueMenu";
import { formatDateTimeRange } from "../../../helpers/dateTimeHelper";
import FileAttachmentsList from "../../files/FileAttachmentsList";
import { LazyExpandableContent } from "../../ui/lazyLoad/LazyExpandableContent";
import { getEmailMessageByIdQuery } from "../../../queries/emailMessageQuery";
import { useApiAccess } from "@repo/hooks/contexts";
import type { RecordId } from "@repo/types/ui/tables";

const activityActionTypeItems: KeyValueItem[] = [
  {
    viewType: KeyValueViewType.iconAndText,
    Id: ActivityActionType.PinToTop,
    Value: "Pin to top",
    icon: <Pin size={12} />,
    classNames: "p-1 mb-1 text-sm hover:bg-gray-100",
  },
  {
    viewType: KeyValueViewType.iconAndText,
    Id: ActivityActionType.Unpin,
    Value: "Unpin",
    icon: <Pin size={12} />,
    classNames: "p-1 mb-1 text-sm hover:bg-gray-100",
  },
  {
    viewType: KeyValueViewType.iconAndText,
    Id: ActivityActionType.Edit,
    Value: "Edit",
    icon: <Pencil size={12} />,
    classNames: "p-1 mb-1 text-sm hover:bg-gray-100",
  },
  {
    viewType: KeyValueViewType.iconAndText,
    Id: ActivityActionType.ReplyAll,
    Value: "Reply All",
    icon: <CornerUpLeft size={12} />,
    classNames: "p-1 mb-1 text-sm hover:bg-gray-100",
  },
  {
    viewType: KeyValueViewType.iconAndText,
    Id: ActivityActionType.Delete,
    Value: "Delete",
    icon: <Trash size={12} />,
    classNames: "p-1 mb-1 text-sm hover:bg-gray-100",
  },
];

function getActivityActionTypeItems(
  timelineActivity: TimelineActivityDTO,
): KeyValueItem[] {
  switch (timelineActivity.ActivityTypeKeyValuePairEnumValue) {
    case TimelineActivityType.Email:
      return activityActionTypeItems.filter(
        (item) =>
          item.Id !== ActivityActionType.Edit &&
          item.Id !== ActivityActionType.Delete &&
          !(
            timelineActivity.PinnedRank == null &&
            item.Id === ActivityActionType.Unpin
          ) &&
          !(
            timelineActivity.PinnedRank != null &&
            item.Id === ActivityActionType.PinToTop
          ),
      );

    case TimelineActivityType.Meeting:
    case TimelineActivityType.CallSummary:
    case TimelineActivityType.Note:
    case TimelineActivityType.Custom:
      return activityActionTypeItems.filter(
        (item) =>
          item.Id !== ActivityActionType.ReplyAll &&
          !(
            timelineActivity.PinnedRank == null &&
            item.Id === ActivityActionType.Unpin
          ) &&
          !(
            timelineActivity.PinnedRank != null &&
            item.Id === ActivityActionType.PinToTop
          ),
      );

    default:
      return activityActionTypeItems.filter(
        (item) =>
          !(
            timelineActivity.PinnedRank == null &&
            item.Id === ActivityActionType.Unpin
          ) &&
          !(
            timelineActivity.PinnedRank != null &&
            item.Id === ActivityActionType.PinToTop
          ),
      );
  }
}

interface ActionTypesProps {
  timelineActivity: TimelineActivityDTO;
  handleActivityActionTypeClick: (
    activityActionType: number,
    activityId: RecordId,
  ) => Promise<void>;
}

const ActionTypes = ({
  timelineActivity,
  handleActivityActionTypeClick,
}: ActionTypesProps) => {
  return (
    timelineActivity.Id != null && (
      <KeyValueMenu
        items={getActivityActionTypeItems(timelineActivity)}
        onValueChange={async (activityActionType) =>
          handleActivityActionTypeClick(
            activityActionType,
            timelineActivity.Id!,
          )
        }
        triggerRenderer={(_, open) => (
          <div
            className={`px-1 py-1 cursor-pointer rounded ${
              open ? "bg-blue-200" : ""
            }`}
          >
            •••
          </div>
        )}
        popupClassNames="w-40"
      />
    )
  );
};

interface TimelineActivityContentProps {
  ticketGuid: string;
  timelineActivity: TimelineActivityDTO;
  handleActivityActionTypeClick: (
    activityActionType: number,
    activityId: RecordId,
  ) => Promise<void>;
}

function TimelineActivityContentContainer({
  ticketGuid,
  timelineActivity,
  handleActivityActionTypeClick,
}: TimelineActivityContentProps) {
  const { data: ticketEmailMessages } = useGetTicketEmailMessages(ticketGuid);
  const { apiClient } = useApiAccess();

  switch (timelineActivity.ActivityTypeKeyValuePairEnumValue) {
    case TimelineActivityType.Email: {
      const emailMessage = ticketEmailMessages?.find(
        (msg) => msg.Id === timelineActivity.EntityId,
      );
      if (!emailMessage) return null;
      return (
        <>
          <div className="mb-5">
            <div className="flex justify-between">
              <div className="text-sm font-bold text-gray-800">
                {emailMessage.FromName} &lt;
                {emailMessage.FromEmail}&gt;
              </div>

              <div className="flex ml-4 text-gray-600 space-x-1">
                <div
                  className="border border-gray-300 rounded p-1 cursor-pointer hover:bg-gray-100"
                  onClick={async () =>
                    handleActivityActionTypeClick(
                      ActivityActionType.ReplyAll,
                      timelineActivity.Id!,
                    )
                  }
                >
                  <CornerUpLeft size={16} />
                </div>
                <div>
                  <ActionTypes
                    timelineActivity={timelineActivity}
                    handleActivityActionTypeClick={
                      handleActivityActionTypeClick
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-2 mb-1">
              <div>
                <User className="inline" size={16} />
              </div>

              <div className="flex flex-col text-sm text-gray-800">
                {emailMessage.ToEmails.map((recipient, i) => (
                  <div key={`to-${recipient.Id}-${i}`} className="flex">
                    <span className="">
                      {recipient.Name} &lt;
                      {recipient.Email}&gt;
                    </span>
                  </div>
                ))}

                {emailMessage.CcEmails.length > 0 &&
                  emailMessage.CcEmails.map((recipient, i) => (
                    <div key={`cc-${recipient.Id}-${i}`} className="flex">
                      <span className="">
                        {recipient.Name} &lt;
                        {recipient.Email}&gt;
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div className="mb-5">
            <strong className="text-sm text-gray-600">
              {emailMessage.Subject}
            </strong>
          </div>

          <FileAttachmentsList
            entityId={emailMessage.EmailMessageGuid!}
            entityType={EntityType.EmailMessage}
          />

          {emailMessage.BodyContent && (
            <LazyExpandableContent
              previewHeight={150}
              previewContent={emailMessage.BodyPreview}
              loadFullDataQuery={getEmailMessageByIdQuery(
                emailMessage.Id!,
                apiClient,
              )}
              classNames="text-sm mt-5"
            />
          )}
        </>
      );
    }

    case TimelineActivityType.Meeting:
    case TimelineActivityType.CallSummary:
    case TimelineActivityType.Note:
    case TimelineActivityType.Custom:
      return (
        <div className="mb-5">
          <div className="flex justify-between mb-2">
            <div className="text-sm font-bold text-gray-800">
              {timelineActivity.CreatedByUser}
            </div>

            <div className="ml-4 text-sm text-gray-600">
              <ActionTypes
                timelineActivity={timelineActivity}
                handleActivityActionTypeClick={handleActivityActionTypeClick}
              />
            </div>
          </div>

          {timelineActivity.ActivityTypeKeyValuePairEnumValue ===
            TimelineActivityType.Meeting && (
            <div className="flex mb-3 text-sm text-gray-700">
              <Clock size={16} />{" "}
              <div className="ml-2 font-bold">Scheduled: </div>
              <div className="ml-1">
                {formatDateTimeRange(
                  timelineActivity.ScheduledDate,
                  timelineActivity.ScheduledStartTime,
                  timelineActivity.ScheduledEndTime,
                )}
              </div>
            </div>
          )}

          <FileAttachmentsList
            entityId={timelineActivity.Id!}
            entityType={EntityType.TimelineActivity}
          />

          {timelineActivity.Content && (
            <LazyExpandableContent
              previewHeight={150}
              previewContent={timelineActivity.Content}
              classNames="text-sm mt-5"
            />
          )}
        </div>
      );

    default:
      return null;
  }
}

export const TimelineActivityContent = ({
  ticketGuid,
  timelineActivity,
  handleActivityActionTypeClick,
}: TimelineActivityContentProps) => {
  return (
    <TimelineActivityContentContainer
      ticketGuid={ticketGuid}
      timelineActivity={timelineActivity}
      handleActivityActionTypeClick={handleActivityActionTypeClick}
    />
  );
};
